import { useCallback, useEffect, useRef, useState } from "react";
import { useOrganization } from "../../../../contexts/OrganizationContext";
import { getWhatsappConnections } from "../../../../services/crmService";
import {
  notifyWhatsappConnectionsUpdated,
  WHATSAPP_CONNECTIONS_UPDATED_EVENT,
} from "../../../../services/modules/whatsappService";
import {
  deleteInstanceCompletely,
  disconnectInstance,
  reconnectInstance,
} from "../../../../services/uazapiService";
import { notify } from "../../../../utils/toast";
import { useWhatsAppConnection } from "./useWhatsAppConnection";

interface DeleteState {
  isOpen: boolean;
  connectionId: string;
  connectionName: string;
  instanceName: string;
}

const CLOSED_DELETE: DeleteState = {
  isOpen: false,
  connectionId: "",
  connectionName: "",
  instanceName: "",
};

/** Lista Uazapi da página de canais: carregar, excluir, desconectar e reconectar. */
export function useWhatsappConnectionsScreen(onAddConnectionRef?: (fn: () => void) => void) {
  const { currentOrganization } = useOrganization();
  const [connections, setConnections] = useState<any[]>([]);
  const connectionsRef = useRef(connections);
  connectionsRef.current = connections;
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deleteState, setDeleteState] = useState<DeleteState>(CLOSED_DELETE);

  const loadConnections = useCallback(async (options?: { silent?: boolean; notify?: boolean }) => {
    const organizationId = currentOrganization?.id;
    if (!organizationId) return;

    if (!options?.silent) setIsLoadingConnections(true);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeoutPromise = new Promise<any[]>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn(
            "[Connections] Timeout ao carregar conexões, recuando...",
          );
          resolve([]);
        }, 15000);
      });

      const data = await Promise.race([
        getWhatsappConnections(organizationId),
        timeoutPromise,
      ]);

      const keepCurrentList =
        Array.isArray(data) && data.length === 0 && connectionsRef.current.length > 0;
      if (keepCurrentList) return;

      const uazapiConnections = Array.isArray(data)
        ? data.filter((connection) => !connection.officialApi)
        : data;
      setConnections(uazapiConnections);
      if (options?.notify) notifyWhatsappConnectionsUpdated();
    } catch (err) {
      console.error("[Connections] Erro ao carregar:", err);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoadingConnections(false);
    }
  }, [currentOrganization?.id]);

  const handleConnectionSuccess = useCallback(() => {
    setShowSuccessModal(true);
    void loadConnections({ notify: true });
  }, [loadConnections]);

  const {
    step,
    setStep,
    isLoading,
    qrCode,
    error,
    startConnection,
    cancelConnection,
    resetFlow,
    setQrCode,
    startStatusPollingForReconnect,
  } = useWhatsAppConnection(handleConnectionSuccess);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    void loadConnections();
  }, [currentOrganization?.id, loadConnections]);

  // Heal de avatar (R2) dispara este evento — recarrega a lista sem spinner.
  useEffect(() => {
    const onUpdated = () => {
      void loadConnections({ silent: true });
    };
    window.addEventListener(WHATSAPP_CONNECTIONS_UPDATED_EVENT, onUpdated);
    return () => {
      window.removeEventListener(WHATSAPP_CONNECTIONS_UPDATED_EVENT, onUpdated);
    };
  }, [loadConnections]);

  useEffect(() => {
    if (onAddConnectionRef) {
      onAddConnectionRef(() => setStep("create"));
    }
  }, [onAddConnectionRef, setStep]);

  const handleDeleteClick = (
    id: string,
    name: string,
    instanceName: string,
  ) => {
    setDeleteState({
      isOpen: true,
      connectionId: id,
      connectionName: name || instanceName || "Conexão",
      instanceName,
    });
  };

  const handleDeleteConfirm = async () => {
    const { connectionId, instanceName } = deleteState;

    try {
      const ok = await deleteInstanceCompletely(instanceName, connectionId);
      if (!ok) throw new Error("delete failed");

      await loadConnections({ notify: true });
      setDeleteState(CLOSED_DELETE);
      notify.success("Conexão removida com sucesso");
    } catch (error) {
      notify.error("Erro ao deletar conexão");
      console.error(error);
    }
  };

  const handleDisconnect = async (instanceName: string) => {
    if (!instanceName) return;

    try {
      const success = await disconnectInstance(instanceName);

      if (success) {
        notify.info(
          "WhatsApp desconectado. Você pode reconectar a qualquer momento.",
        );
      } else {
        notify.warning("Não foi possível desconectar. Tente novamente.");
      }

      await loadConnections({ notify: true });
    } catch (error) {
      notify.error("Erro ao desconectar");
      console.error(error);
    }
  };

  const handleReconnect = async (
    instanceName: string,
    integrationId: string,
  ) => {
    if (!instanceName) return;

    try {
      setQrCode(null);
      setStep("qrcode");
      notify.info("Gerando QR Code para reconexão...");

      const qrData = await reconnectInstance(instanceName);

      if ((qrData as any)?.alreadyConnected) {
        notify.success("WhatsApp já está conectado!");
        setStep("list");
        await loadConnections({ notify: true });
        return;
      }

      if (qrData?.base64 || qrData?.code) {
        setQrCode(qrData.base64 || qrData.code || "");
        startStatusPollingForReconnect(instanceName, integrationId);
      } else {
        notify.warning("Não foi possível gerar QR Code. Tente novamente.");
        setStep("list");
      }
    } catch (error) {
      notify.error("Erro ao gerar QR Code");
      console.error(error);
      setStep("list");
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setStep("list");
    void loadConnections({ notify: true });
  };

  return {
    step,
    setStep,
    isLoading,
    qrCode,
    error,
    startConnection,
    cancelConnection,
    resetFlow,
    connections,
    isLoadingConnections,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDisconnect,
    handleReconnect,
    deleteState,
    closeDelete: () => setDeleteState(CLOSED_DELETE),
    showSuccessModal,
    closeSuccessModal,
  };
}
